'use client'

// imports
import Link from 'next/link';

// lib files
import { PASSPORT_LOOKUP_URL } from '@/lib/constants';
import { StationData } from "@/lib/types/api/stations-data";
import { useHasMounted } from '@/lib/hooks';
import { useIdentity } from '@/lib/hooks/useIdentity';

// components
import ITSPicture from "@/components/ITSPicture/ITSPicture";
import PassportDonateButton from '@/components/PassportDonateButton/PassportDonateButton';
import PassportLockup from '@/components/PassportLearnMore/PassportLockup/PassportLockup';

// styles
import styles from './StationPassportRow.module.scss';

interface StationPassportRowProps {
  stationData: StationData;
  depIsLocalizedPassportMember?: boolean;
}

const StationPassportRow = (props: StationPassportRowProps) => {
  const { stationData, depIsLocalizedPassportMember } = props;
  const { attributes } = stationData;
  const { short_common_name, images } = attributes;
  const fallBackStationBackgroundImage = 'https://image.pbs.org/webobjects/my-station-hero-fallback-image-MhXCtL3.jpg'

  const hasMounted = useHasMounted();
  const { cachedIdentity } = useIdentity();

  const isLocalizedPassportMember = depIsLocalizedPassportMember ?? (hasMounted
    ? (cachedIdentity?.isPassportMemberOf(stationData) ?? false)
    : false);

  // get stationImages object with a profile of "station-scenery-169", falling back to  "station-scenery-11"
  const stationBackgroundImage =
    images.find(image => image.profile === "station-scenery-169")?.url ||
    images.find(image => image.profile === "station-scenery-11")?.url ||
    fallBackStationBackgroundImage;

  return (
    <div className={styles.station_passport_row} data-testid="station-passport-row">
      { stationBackgroundImage && (
      <ITSPicture
        src={stationBackgroundImage}
        alt={""}
        width={1700}
        height={956}
        sources={[
          {
            minWidth: 1024,
            width: 1024,
            height: 200,
            focalcrop: '50x50',
          },
          {
            minWidth: 768,
            width: 768,
            height: 200,
            focalcrop: '50x50',
          },
          {
            minWidth: 400,
            width: 400,
            height: 250,
            focalcrop: '50x50',
          },
          {
            minWidth: 320,
            width: 320,
            height: 250,
          },
        ]}
        ariaHidden={true}
        className={styles.station_passport_row_background_image}
      />
      )}
      <div className={styles.station_passport_row_overlay}>

        <PassportLockup
          stationData={stationData}
          element="h2"
          className={styles.station_passport_row_title}
        />

        <p className={styles.station_passport_row_text}>
          {isLocalizedPassportMember
            ? 'Thank you for being a member! You are supporting America\'s largest classroom, the nation\'s largest stage for the arts, and a trusted window into the world.'
            : `Become a member of ${short_common_name}, support your local community, and get extended access to your favorite PBS shows, films, and specials.`}
        </p>

        <PassportDonateButton 
          gtmEventDataObjectLocation='station passport row'
          stationData={stationData}
          depUserIsLocalizedPassport={depIsLocalizedPassportMember}
        />

        {!isLocalizedPassportMember && (
          <>
            <p className={styles.member_lookup}>
              <span>{`Already a Member of ${short_common_name}?`}</span>
              <Link href={PASSPORT_LOOKUP_URL}>
                Check Your Passport Status
              </Link>
            </p>
          </>
        )}

      </div>

    </div>
  );
};

export default StationPassportRow;
